import { useMemo } from 'react';

import {
  StoreItemWithOptionalInventorySummary,
  useShowConsumerStoreItemInventoryViaOptions,
} from '@givebacks/api/store';
import { useFormyValidator } from '@givebacks/formy';
import { UseAdditionalFieldsForm } from 'ui';
import { useDidUpdate } from 'ui/Hooks';

type StoreItemInventoryWrapperProps<T extends string> = {
  storeItem: StoreItemWithOptionalInventorySummary;
  storeId: string;
  /**
   * When provided, will use the formId to get the additional fields form,
   * allowing the hook to determine if a variant is out of stock
   */
  additionalFieldsFormId: T;
  children: (props: {
    isOutOfStock: boolean;
    isLowInventory: boolean;
    inventoryCount: number;
  }) => React.ReactNode;
  /**
   * Callback to be triggered if new variant is selected
   */
  onVariantChange?: () => void;
};

type Forms<T extends string> = { [K in T]: UseAdditionalFieldsForm };

const StoreItemInventoryWrapper = <T extends string>({
  storeItem,
  storeId,
  additionalFieldsFormId,
  onVariantChange,
  children,
}: StoreItemInventoryWrapperProps<T>) => {
  const { useGetForm } = useFormyValidator<Forms<T>>();
  const additionalFieldsForm = useGetForm(additionalFieldsFormId);
  const additionalFields = useMemo(
    () => additionalFieldsForm?.values.options ?? [],
    [additionalFieldsForm]
  );

  const variants = useMemo(
    () => additionalFields.filter((option) => !!option.manages_inventory) ?? [],
    [additionalFields]
  );

  const variantSearches = useMemo(() => {
    const searches = variants
      .filter((variant) => !!variant.value)
      .reduce<{
        [key: string]: string;
      }>(
        (acc, variant) => {
          acc[variant.name] = variant.value as string;
          return acc;
        },
        {} as { [key: string]: string }
      );

    return searches;
  }, [variants]);

  /**
   * Stable hash used to check changes in variant selections. Orginally, we relied on the variants array directly,
   * however updates to even custom fields leads to the form updating and, ultimately, creating a new array of variants.
   * Even though the contents are the same, referential equality is lost, thus causing the `onVariantChange` callback to trigger.
   */
  const selectedVariantHash = useMemo(() => {
    return JSON.stringify(
      [...variants]
        .sort((variantA, variantB) => variantA.key.localeCompare(variantB.key))
        .map((variant) => ({
          value: Array.isArray(variant.value) ? [...variant.value].sort() : variant.value,
        }))
    );
  }, [variants]);

  // enable query for inventory - only if the user has chosen a specific variant
  const enabled = useMemo(() => {
    if (!variants || variants.length === 0) return false;
    return Object.keys(variantSearches).length === variants.length;
  }, [variantSearches, variants]);

  // fetch the inventory for the chosen variant
  const { data: variantInventory } = useShowConsumerStoreItemInventoryViaOptions({
    causeId: storeId,
    uuid: storeItem.uuid,
    options: variantSearches,
    enabled,
  });

  /**
   * the main return value of this hook
   */
  const isOutOfStock = useMemo(() => {
    // if inventory tracking is not enabled, return false
    if (!storeItem.manage_inventory || !storeItem.inventory_summary) return false;

    // if the inventory summary amount is 0, return true
    if (storeItem.inventory_summary?.amount === 0) return true;

    // if the variant inventory is not found, return false
    if (!variantInventory) return false;

    // if the variant inventory amount is 0, return true
    if (variantInventory.amount === 0) return true;

    return false;
  }, [storeItem, variantInventory]);

  // if there are no variants, return the storeItem inventory count, else return the amount of the selected variant
  const inventoryCount = useMemo(() => {
    if (storeItem.inventory_summary?.count === 1) {
      return storeItem.inventory_summary.amount ?? 0;
    }

    return variantInventory?.amount ?? 0;
  }, [
    storeItem.inventory_summary?.amount,
    storeItem.inventory_summary?.count,
    variantInventory?.amount,
  ]);

  const isLowInventory = useMemo(() => {
    // if inventory tracking is not enabled, return false
    if (!storeItem?.manage_inventory || !storeItem?.inventory_summary) {
      return false;
    }
    return inventoryCount > 0 && inventoryCount <= 10;
  }, [storeItem?.inventory_summary, storeItem?.manage_inventory, inventoryCount]);

  // Uses a stable hash of the select variants to conditionally call the provided callback
  useDidUpdate(() => {
    onVariantChange?.();
  }, [selectedVariantHash]);

  return children({ isOutOfStock, isLowInventory, inventoryCount });
};

export { StoreItemInventoryWrapper };
